import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Button, Card, Row } from "reactstrap";
import { motion } from "framer-motion";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Col,
  Icon,
} from "../../../components/Component";
import Head from "../../../layout/head/Head";
import Content from "../../../layout/content/Content";
import AuthLoader from "../../components/page_messaging/AuthLoader";
import AuthError from "../../components/page_messaging/AuthError";
import AuthNoData from "../../components/page_messaging/AuthNoData";
import InventoryStatistics from "./InventoryStatistics";
import { RETRIEVE_HOMEPAGE_ANALYTICAL_DATA_API } from "../../../api/api_data";
import { employee_dashboard_actions_helper } from "../../../actions/actions_helper";
import {
  fail_retrieve_home_page_analytical_data,
  loading_retrieve_home_page_analytical_data,
  reset_retrieve_home_page_analytical_data,
  success_retrieve_home_page_analytical_data,
} from "../../../reducers/dashboard_reducers/warehouse/retrieve_home_page_analytical_data_Slice";
import {
  RETRIEVE_DATA_ACCESS_DENIED_MSG,
  RETRIEVE_DATA_ERR_MSG,
  RETRIEVE_DATA_SUCCESS_MSG,
} from "../../../constants/success_error_messages_consts";
import { messageToast } from "../../../utils/Utils";
import * as app_consts from "../../../constants/react_app_consts";

const DashboardIndex = () => {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page_data, setPageData] = useState(null);

  useEffect(() => {
    retrieve_page_data();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const retrieve_page_data = () => {
    setError("");
    dispatch(
      employee_dashboard_actions_helper(
        loading_retrieve_home_page_analytical_data,
        success_retrieve_home_page_analytical_data,
        fail_retrieve_home_page_analytical_data,
        RETRIEVE_DATA_ERR_MSG,
        RETRIEVE_DATA_SUCCESS_MSG,
        app_consts.GET,
        RETRIEVE_HOMEPAGE_ANALYTICAL_DATA_API,
        null,
        RETRIEVE_DATA_ACCESS_DENIED_MSG
      )
    );
  };

  const retrieve_home_page_analytical_data_details = useSelector(
    (state) =>
      state.retrieve_home_page_analytical_data
        ?.retrieve_home_page_analytical_data
  );

  useEffect(() => {
    if (!retrieve_home_page_analytical_data_details) return;

    if (retrieve_home_page_analytical_data_details.status !== app_consts.IDLE) {
      if (retrieve_home_page_analytical_data_details.status === app_consts.LOADING) {
        setError("");
        setLoading(true);
      } else {
        setLoading(false);

        if (retrieve_home_page_analytical_data_details.status === app_consts.FAIL) {
          const msg =
            retrieve_home_page_analytical_data_details.message || RETRIEVE_DATA_ERR_MSG;
          setError(msg);
          messageToast(msg, app_consts.FAIL);
        } else if (retrieve_home_page_analytical_data_details.status === app_consts.SUCCESS) {
          setPageData(retrieve_home_page_analytical_data_details.data || {});
        }

        dispatch(reset_retrieve_home_page_analytical_data());
      }
    }
  }, [retrieve_home_page_analytical_data_details, dispatch]);

  const refreshList = () => retrieve_page_data();

  // Animation variants
  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.12, duration: 0.35 },
    }),
    hover: {
      y: -6,
      boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
      transition: { duration: 0.2 },
    },
  };

  const dashboardCards = [
    {
      title: "Total Goods Received Notes",
      value: page_data?.total_grns ?? "0",
      icon: "inbox",
      color: "#6576ff",
      link: "/dashboard/goods_received",
    },
    {
      title: "Total Delivery Notes",
      value: page_data?.total_dns ?? "0",
      icon: "truck",
      color: "#1ee0ac",
      link: "/dashboard/delivery_notes",
    },
    {
      title: "Total Loading Orders",
      value: page_data?.total_loading_orders ?? "0",
      icon: "upload-cloud",
      color: "#f4bd0e",
      link: "/dashboard/loading_orders",
    },

    {
      title: "Total User Accounts",
      value: page_data?.total_user_accounts ?? "0",
      icon: "users",
      color: "#ef4b4b",
      link: "/dashboard/user/accounts",
    },
  ];

  return (
    <>
      <Head title="Dashboard" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Dashboard</BlockTitle>
              <BlockDes className="text-soft"></BlockDes>

              {!loading && (
                <div className="mt-2">
                  <Link
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      refreshList();
                    }}
                  >
                    <Button
                      color="light"
                      outline
                      className="bg-white d-none d-sm-inline-flex"
                    >
                      <span>Refresh List</span>
                    </Button>
                  </Link>
                </div>
              )}
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        {loading ? (
          <AuthLoader loading_message="Loading Data..." />
        ) : error ? (
          <AuthError error_message={error} />
        ) : page_data ? (
          <Block>
            <Row className="g-gs">
              {/*  2 CARDS PER ROW FROM md AND UP */}
              {dashboardCards.map((card, index) => (
                <Col sm="12" md="6" key={index} style={{ marginBottom: "20px" }}>
                  <motion.div
                    custom={index}
                    variants={cardVariants}
                    initial="hidden"
                    animate="visible"
                    whileHover="hover"
                    style={{ height: "100%" }}
                  >
                    <Card
                      tag={Link}
                      to={card.link}
                      style={{
                        borderRadius: "12px",
                        border: "1px solid #e5e9f2",
                        height: "100%",
                        textDecoration: "none",
                        cursor: "pointer",
                      }}
                    >
                      <div style={{ padding: "20px" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "12px",
                          }}
                        >
                          <div>
                            <h6
                              style={{
                                fontSize: "14px",
                                color: "#6c757d",
                                marginBottom: "8px",
                                fontWeight: 600,
                              }}
                            >
                              {card.title}
                            </h6>
                            <h2
                              style={{
                                fontSize: "26px",
                                fontWeight: "800",
                                margin: 0,
                                color: "#1f2b3a",
                              }}
                            >
                              {card.value}
                            </h2>
                          </div>

                          <div
                            style={{
                              width: "52px",
                              height: "52px",
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "26px",
                              backgroundColor: `${card.color}15`,
                              flexShrink: 0,
                            }}
                          >
                            <Icon name={card.icon} style={{ color: card.color }} />
                          </div>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                </Col>
              ))}

              {/* Full width chart section */}
              <Col md="12">
                <InventoryStatistics data={page_data} />
              </Col>
            </Row>
          </Block>
        ) : (
          <AuthNoData no_data_message={"No Data Found."} />
        )}
      </Content>
    </>
  );
};

export default DashboardIndex;
